import React, { useCallback, useEffect, useState } from 'react';
import {
  Check,
  ChevronRight,
  ExternalLink,
  FileText,
  Folder,
  Loader2,
  LogIn,
  RefreshCw,
  Search,
} from 'lucide-react';
import { apiService } from '../../../services/api';

const DRIVE_SEARCH_MIN_CHARS = 2;

const formatFileSize = (size) => {
  if (!size) return '';
  if (size < 1024) return `${size} o`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} Ko`;
  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
};

const formatModifiedDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR');
};

const createDriveReference = (item) => ({
  drive_id: item.id,
  name: item.name,
  mime_type: item.mime_type,
  is_folder: item.is_folder,
  web_view_link: item.web_view_link,
});

const DriveBrowserPanel = ({
  projectId,
  project,
  selectable = false,
  onSelect,
  className = '',
  maxHeight = '520px',
}) => {
  const [status, setStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [folderStack, setFolderStack] = useState([]);
  const [rootFolderId, setRootFolderId] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeContentSearch, setIncludeContentSearch] = useState(false);
  const [searchRefreshKey, setSearchRefreshKey] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const hasDriveFolder = Boolean(project?.google_drive_url);
  const currentFolder = folderStack[folderStack.length - 1] || null;
  const trimmedSearchTerm = searchTerm.trim();
  const isSearchActive = trimmedSearchTerm.length >= DRIVE_SEARCH_MIN_CHARS;
  const isSearchPending = trimmedSearchTerm.length > 0 && !isSearchActive;
  const displayedFiles = isSearchActive ? searchResults : files;
  const isListLoading = isSearchActive ? loadingSearch : loadingFiles;

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const data = await apiService.getGoogleDriveStatus();
      setStatus(data);
    } catch (err) {
      setStatus({ connected: false });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadFiles = useCallback(
    async ({ folderId = null, append = false, pageToken = null } = {}) => {
      if (!hasDriveFolder) {
        setFiles([]);
        return;
      }
      if (append) {
        setLoadingMore(true);
      } else {
        setLoadingFiles(true);
      }
      setError(null);
      try {
        const data = await apiService.getProjectDriveFiles(projectId, {
          folderId,
          pageToken,
          pageSize: 100,
        });
        setRootFolderId(data.root_folder_id || null);
        setNextPageToken(data.next_page_token || null);
        setFiles((prev) => (append ? [...prev, ...(data.files || [])] : data.files || []));
        setStatus((prev) => ({ ...(prev || {}), connected: true }));
      } catch (err) {
        setFiles([]);
        setNextPageToken(null);
        setError(err.message || 'Erreur Drive');
        if (/connect|renouveler|Drive/i.test(err.message || '')) {
          setStatus((prev) => ({ ...(prev || {}), connected: false }));
        }
      } finally {
        setLoadingFiles(false);
        setLoadingMore(false);
      }
    },
    [hasDriveFolder, projectId],
  );

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    setFolderStack([]);
    setSearchTerm('');
    setSearchResults([]);
    setIncludeContentSearch(false);
    if (status?.connected && hasDriveFolder) {
      loadFiles({ folderId: null });
    }
  }, [projectId, project?.google_drive_url, status?.connected, hasDriveFolder, loadFiles]);

  useEffect(() => {
    if (!isSearchActive) {
      setSearchResults([]);
      setLoadingSearch(false);
      if (trimmedSearchTerm) {
        setError(null);
      }
      return undefined;
    }
    if (!status?.connected || !hasDriveFolder) {
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    setLoadingSearch(true);
    setError(null);

    const timeoutId = window.setTimeout(async () => {
      try {
        const data = await apiService.searchProjectDriveFiles(projectId, {
          query: trimmedSearchTerm,
          recursive: true,
          searchContent: includeContentSearch,
          maxResults: 30,
          signal: controller.signal,
        });
        if (cancelled) return;
        setSearchResults(data.files || []);
        setStatus((prev) => ({ ...(prev || {}), connected: true }));
      } catch (err) {
        if (cancelled || err.name === 'AbortError') return;
        setSearchResults([]);
        setError(err.message || 'Recherche Drive impossible');
        if (/connect|renouveler|Drive/i.test(err.message || '')) {
          setStatus((prev) => ({ ...(prev || {}), connected: false }));
        }
      } finally {
        if (!cancelled) {
          setLoadingSearch(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [
    trimmedSearchTerm,
    isSearchActive,
    includeContentSearch,
    searchRefreshKey,
    projectId,
    status?.connected,
    hasDriveFolder,
  ]);

  const handleConnect = async () => {
    setError(null);
    try {
      const data = await apiService.startGoogleDriveAuth(window.location.href);
      if (data?.authorization_url) {
        window.location.assign(data.authorization_url);
      }
    } catch (err) {
      setError(err.message || 'Connexion Drive impossible');
    }
  };

  const openFolder = (item) => {
    const nextStack = [...folderStack, { id: item.id, name: item.name }];
    setFolderStack(nextStack);
    setSearchTerm('');
    loadFiles({ folderId: item.id });
  };

  const openBreadcrumb = (index) => {
    if (index < 0) {
      setFolderStack([]);
      loadFiles({ folderId: null });
      return;
    }
    const nextStack = folderStack.slice(0, index + 1);
    setFolderStack(nextStack);
    loadFiles({ folderId: nextStack[nextStack.length - 1]?.id || null });
  };

  const refreshCurrentView = () => {
    if (isSearchActive) {
      setSearchRefreshKey((value) => value + 1);
      return;
    }
    loadFiles({ folderId: currentFolder?.id || null });
  };

  const openItem = (item) => {
    if (item.is_folder) {
      openFolder(item);
      return;
    }
    if (item.web_view_link) {
      window.open(item.web_view_link, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSelect = (item) => {
    if (onSelect) {
      onSelect(createDriveReference(item));
    }
  };

  if (!hasDriveFolder) {
    return (
      <div className={`rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 ${className}`}>
        Dossier Drive non configure sur ce projet.
      </div>
    );
  }

  if (loadingStatus && !status) {
    return (
      <div className={`flex items-center gap-2 text-sm text-slate-500 ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Chargement Drive...
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className={`rounded-md border border-slate-200 bg-white p-4 ${className}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-slate-800">Google Drive</p>
            <p className="mt-1 text-xs text-slate-500">Connexion requise pour parcourir le dossier projet.</p>
          </div>
          <button
            type="button"
            onClick={handleConnect}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <LogIn className="h-4 w-4" />
            Connecter Drive
          </button>
        </div>
        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div className={`flex min-h-0 flex-col rounded-md border border-slate-200 bg-white ${className}`}>
      <div className="border-b border-slate-200 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1 text-sm text-slate-600">
            <button
              type="button"
              onClick={() => openBreadcrumb(-1)}
              className="inline-flex min-w-0 items-center gap-1 rounded px-1.5 py-1 font-medium text-slate-800 hover:bg-slate-100"
              title={project?.name || 'Projet'}
            >
              <Folder className="h-4 w-4 flex-none text-amber-500" />
              <span className="truncate">{project?.name || 'Projet'}</span>
            </button>
            {folderStack.map((folder, index) => (
              <React.Fragment key={folder.id}>
                <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                <button
                  type="button"
                  onClick={() => openBreadcrumb(index)}
                  className="max-w-[180px] truncate rounded px-1.5 py-1 hover:bg-slate-100"
                  title={folder.name}
                >
                  {folder.name}
                </button>
              </React.Fragment>
            ))}
          </div>
          <button
            type="button"
            onClick={refreshCurrentView}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            title="Rafraichir"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Rafraichir
          </button>
        </div>
        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full rounded-md border border-slate-200 py-2 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Rechercher un fichier ou dossier dans tout le projet"
          />
        </div>
        <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={includeContentSearch}
            onChange={(event) => setIncludeContentSearch(event.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          Chercher aussi dans le contenu des documents
        </label>
        {isSearchActive && (
          <p className="mt-2 text-xs text-slate-500">
            {includeContentSearch
              ? 'Recherche dans les noms, sous-dossiers et contenu indexé des documents Drive.'
              : 'Recherche rapide dans les noms de fichiers et sous-dossiers.'}
          </p>
        )}
        {isSearchPending && (
          <p className="mt-2 text-xs text-slate-500">
            Tape au moins {DRIVE_SEARCH_MIN_CHARS} caractères pour lancer la recherche Drive complète.
          </p>
        )}
      </div>

      <div className="min-h-[180px] overflow-y-auto" style={{ maxHeight }}>
        {isListLoading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            {isSearchActive ? 'Recherche Drive...' : 'Chargement des fichiers...'}
          </div>
        ) : displayedFiles.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {displayedFiles.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50"
              >
                <button
                  type="button"
                  onClick={() => openItem(item)}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                  {item.is_folder ? (
                    <Folder className="h-5 w-5 flex-none text-amber-500" />
                  ) : (
                    <FileText className="h-5 w-5 flex-none text-blue-500" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-800">
                      {item.name}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-slate-500">
                      {[formatModifiedDate(item.modified_time), formatFileSize(item.size)]
                        .filter(Boolean)
                        .join(' - ')}
                    </span>
                  </span>
                </button>
                {!item.is_folder && item.web_view_link && (
                  <a
                    href={item.web_view_link}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    title="Ouvrir dans Drive"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                {selectable && (
                  <button
                    type="button"
                    onClick={() => handleSelect(item)}
                    className="inline-flex items-center gap-1 rounded-md border border-blue-200 px-2 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
                  >
                    <Check className="h-3.5 w-3.5" />
                    Referencer
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="py-10 text-center text-sm text-slate-500">
            {isSearchActive
              ? includeContentSearch
                ? 'Aucun résultat dans le Drive du projet.'
                : 'Aucun fichier ou dossier trouvé par nom.'
              : 'Aucun fichier dans ce dossier.'}
          </div>
        )}
      </div>

      {((!isSearchActive && nextPageToken) || error) && (
        <div className="border-t border-slate-200 p-3">
          {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
          {!isSearchActive && nextPageToken && (
            <button
              type="button"
              onClick={() =>
                loadFiles({
                  folderId: currentFolder?.id || rootFolderId,
                  append: true,
                  pageToken: nextPageToken,
                })
              }
              disabled={loadingMore}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
              Charger plus
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DriveBrowserPanel;
